import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { formatEur, type InvoiceDTO } from '@wf/shared'
import { api } from '../api/client'
import { buildCsv, downloadTextFile } from '../export/csv'
import { formatDateDE } from '../time/format'

/** Rechnungsliste als CSV (Excel-DE) — rein aus den geladenen Daten, kein zusätzlicher Endpunkt. */
function exportInvoicesCsv(invoices: readonly InvoiceDTO[]): void {
  const headers = ['Rechnungsnr.', 'Datum', 'Auftrag', 'Kennzeichen', 'Kunde', 'Betrag', 'Bezahlt', 'Zahlart']
  const rows = invoices.map((invoice) => [
    invoice.invNo ?? '',
    invoice.invoicedAt ? formatDateDE(invoice.invoicedAt) : '',
    invoice.nr ?? '',
    invoice.plate ?? '',
    invoice.customerName ?? '',
    formatEur(invoice.valueCents),
    invoice.paidAt ? 'ja' : 'nein',
    invoice.paidMethod ?? '',
  ])
  downloadTextFile('rechnungen.csv', buildCsv(headers, rows))
}

/** Rechnungen: festgeschriebene §14-Rechnungen mit Summe/„davon bezahlt". Read-only; jede Zeile öffnet
 *  den Auftrag. Geld über @wf/shared (formatEur). CSV-Export aus den geladenen Daten. */
export function InvoicesPage() {
  const { data, isLoading, error } = useQuery({ queryKey: ['invoices'], queryFn: api.listInvoices })

  if (isLoading) return <div className="muted">Rechnungen werden geladen…</div>
  if (error) {
    return (
      <div className="error" role="alert">
        Rechnungen konnten nicht geladen werden.
      </div>
    )
  }
  const invoices = data?.invoices ?? []

  return (
    <section className="wf-view">
      <div className="wf-viewhead">
        <div>
          <h2 className="viewtitle">Rechnungen</h2>
          <p className="sub">Festgeschriebene §14-Rechnungen</p>
        </div>
        {invoices.length > 0 ? (
          <button type="button" className="wf-btn wf-btn--ghost wf-viewhead__actions" onClick={() => exportInvoicesCsv(invoices)}>
            CSV exportieren
          </button>
        ) : null}
      </div>
      {data && data.count > 0 ? (
        <div className="wf-billsummary">
          <div className="wf-stat">
            <div className="wf-stat__value">{data.count}</div>
            <div className="wf-stat__sub">Rechnungen</div>
          </div>
          <div className="wf-stat">
            <div className="wf-stat__value wf-stat__value--sm">{formatEur(data.totalCents)}</div>
            <div className="wf-stat__sub">Summe</div>
          </div>
          <div className="wf-stat">
            <div className="wf-stat__value wf-stat__value--sm wf-stat__value--paid">{formatEur(data.paidCents)}</div>
            <div className="wf-stat__sub">davon bezahlt</div>
          </div>
        </div>
      ) : null}
      {invoices.length === 0 ? (
        <div className="wf-empty">
          <div className="wf-empty__title">Noch keine Rechnungen</div>
          <div className="wf-empty__text">Eine Rechnungsnummer entsteht beim Festschreiben im Auftrag.</div>
        </div>
      ) : (
        <div className="wf-rowlist">
          {invoices.map((invoice) => (
            <Link key={invoice.id} to={`/print/invoice-doc/${invoice.id}`} className="wf-card wf-billrow">
              <span className="wf-tnum wf-billrow__no">{invoice.invNo ?? '—'}</span>
              {invoice.type === 'cancellation' ? (
                <span className="wf-tag wf-tag--storno">Storno</span>
              ) : invoice.cancelled ? (
                <span className="wf-tag wf-tag--cancelled">storniert</span>
              ) : null}
              {invoice.plate ? (
                <span className="wf-plate">
                  <span className="wf-plate__eu">D</span>
                  <span className="wf-plate__nr">{invoice.plate}</span>
                </span>
              ) : null}
              <span className="wf-billrow__cust">{invoice.customerName ?? ''}</span>
              <span className="wf-billrow__end">
                <span className="wf-tnum">{formatEur(invoice.valueCents)}</span>
                {invoice.type === 'invoice' ? (
                  <span className={invoice.paidAt ? 'wf-card__fact--paid' : 'wf-card__fact--due'}>
                    {invoice.paidAt ? '✓ bezahlt' : '€ offen'}
                  </span>
                ) : null}
              </span>
            </Link>
          ))}
        </div>
      )}
    </section>
  )
}
